import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { listRepairServices } from '@/lib/api/repair-services.js';
import type { TicketDevice, TicketRepair, PatchTicketDeviceBody } from '@/lib/api/tickets.js';
import { formatCurrency } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

const CONDITIONS = ['excellent', 'good', 'fair', 'poor', 'broken'] as const;
const PASSCODE_TYPES = [
  'pin',
  'pattern',
  'password',
  'face_id',
  'fingerprint',
  'none',
  'unknown',
] as const;

/** Types that need a staff-entered code/pattern value */
function passcodeTypeNeedsValue(type: string | null | undefined): boolean {
  const t = (type ?? '').toLowerCase().trim().replace(/\s+/g, '_');
  return t === 'pin' || t === 'password' || t === 'pattern' || t === 'fingerprint';
}

function passcodeTypeClearsValue(type: string | null | undefined): boolean {
  const t = (type ?? '').toLowerCase().trim().replace(/\s+/g, '_');
  return t === 'none' || t === 'face_id' || t === 'unknown';
}

export type DeviceEditPricePrompt = {
  repairs: Array<{
    repairId: string;
    serviceName: string;
    previousPrice: string;
    newPrice: string;
  }>;
  body: PatchTicketDeviceBody;
};

type Props = {
  open: boolean;
  device: TicketDevice;
  repairs: TicketRepair[];
  canEdit: boolean;
  onClose: () => void;
  onSave: (body: PatchTicketDeviceBody) => void;
  /** Called when identity change would change prices — parent shows confirm then saves */
  onPriceConfirmNeeded: (prompt: DeviceEditPricePrompt) => void;
};

export function EditTicketDeviceModal({
  open,
  device,
  repairs,
  canEdit,
  onClose,
  onSave,
  onPriceConfirmNeeded,
}: Props) {
  const [makeId, setMakeId] = useState(device.makeId ?? '');
  const [modelId, setModelId] = useState(device.modelId ?? '');
  const [customModelName, setCustomModelName] = useState(device.customModelName ?? '');
  const [imei, setImei] = useState(device.imei ?? '');
  const [serialNumber, setSerialNumber] = useState(device.serialNumber ?? '');
  const [color, setColor] = useState(device.color ?? '');
  const [conditionAtIntake, setConditionAtIntake] = useState(device.conditionAtIntake ?? 'good');
  const [passcodeType, setPasscodeType] = useState(device.passcodeType ?? 'unknown');
  const [passcode, setPasscode] = useState('');
  const [makeSearch, setMakeSearch] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMakeId(device.makeId ?? '');
    setModelId(device.modelId ?? '');
    setCustomModelName(device.customModelName ?? '');
    setImei(device.imei ?? '');
    setSerialNumber(device.serialNumber ?? '');
    setColor(device.color ?? '');
    setConditionAtIntake(device.conditionAtIntake ?? 'good');
    setPasscodeType(device.passcodeType ?? 'unknown');
    setPasscode('');
    setMakeSearch('');
    setModelSearch('');
  }, [open, device]);

  const makesQ = useQuery({
    queryKey: ['device-makes'],
    queryFn: () => listDeviceMakes(),
    enabled: open,
  });
  const modelsQ = useQuery({
    queryKey: ['device-models', makeId],
    queryFn: () => listDeviceModels(makeId ? { makeId } : {}),
    enabled: open && Boolean(makeId),
  });

  const filteredMakes = useMemo(() => {
    const list = makesQ.data ?? [];
    const q = makeSearch.trim().toLowerCase();
    if (!q) return list.slice(0, 40);
    return list.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 40);
  }, [makesQ.data, makeSearch]);

  const filteredModels = useMemo(() => {
    const list = modelsQ.data ?? [];
    const q = modelSearch.trim().toLowerCase();
    if (!q) return list.slice(0, 60);
    return list.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 60);
  }, [modelsQ.data, modelSearch]);

  if (!open) return null;

  async function handleSubmit() {
    if (!canEdit) return;
    const body: PatchTicketDeviceBody = {
      makeId: makeId || null,
      modelId: modelId || null,
      customModelName: customModelName.trim() || null,
      imei: imei.replace(/\D/g, '').slice(0, 15) || null,
      serialNumber: serialNumber.trim() || null,
      color: color.trim() || null,
      conditionAtIntake,
      passcodeType,
    };
    if (passcodeTypeNeedsValue(passcodeType)) {
      // Blank means keep existing value on file
      if (passcode.trim()) body.passcode = passcode.trim();
    } else if (passcodeTypeClearsValue(passcodeType)) {
      body.passcode = null;
    }

    const identityChanged =
      (makeId || null) !== (device.makeId ?? null) ||
      (modelId || null) !== (device.modelId ?? null) ||
      (customModelName.trim() || null) !== (device.customModelName ?? null);

    if (identityChanged && repairs.some((r) => r.serviceId) && (makeId || modelId)) {
      setBusy(true);
      try {
        const services = await listRepairServices({
          ...(makeId ? { makeId } : {}),
          ...(modelId ? { modelId } : {}),
          displayOnPos: false,
          requireModelPricing: false,
        });
        const byId = new Map(services.map((s) => [s.id, s]));
        const diffs: DeviceEditPricePrompt['repairs'] = [];
        for (const repair of repairs) {
          if (!repair.serviceId) continue;
          const svc = byId.get(repair.serviceId);
          if (!svc) continue;
          const newPrice = Number(svc.resolvedPrice ?? svc.defaultPrice ?? svc.price ?? 0);
          const prev = Number(repair.quotedPrice);
          if (Math.abs(newPrice - prev) < 0.005) continue;
          diffs.push({
            repairId: repair.id,
            serviceName: repair.serviceName,
            previousPrice: prev.toFixed(2),
            newPrice: newPrice.toFixed(2),
          });
        }
        if (diffs.length > 0) {
          onPriceConfirmNeeded({ repairs: diffs, body });
          return;
        }
      } finally {
        setBusy(false);
      }
    }

    onSave(body);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-bold text-slate-950">Edit device</h3>
        {!canEdit ? (
          <p className="mt-2 text-sm text-amber-800">
            This ticket is closed. Devices cannot be modified.
          </p>
        ) : null}

        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-600">Manufacturer</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="Search make…"
              value={makeSearch}
              onChange={(e) => setMakeSearch(e.target.value)}
              disabled={!canEdit}
            />
            <div className="mt-1 max-h-28 overflow-y-auto rounded-lg border border-slate-200">
              {filteredMakes.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={!canEdit}
                  className={cn(
                    'block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50',
                    makeId === m.id && 'bg-primary/10 font-semibold text-primary',
                  )}
                  onClick={() => {
                    setMakeId(m.id);
                    setModelId('');
                    setCustomModelName('');
                    setMakeSearch(m.name);
                  }}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-slate-600">Model</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="Search model…"
              value={modelSearch}
              onChange={(e) => setModelSearch(e.target.value)}
              disabled={!canEdit || !makeId}
            />
            <div className="mt-1 max-h-28 overflow-y-auto rounded-lg border border-slate-200">
              {filteredModels.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={!canEdit}
                  className={cn(
                    'block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50',
                    modelId === m.id && 'bg-primary/10 font-semibold text-primary',
                  )}
                  onClick={() => {
                    setModelId(m.id);
                    setCustomModelName('');
                    setModelSearch(m.name);
                  }}
                >
                  {m.name}
                </button>
              ))}
            </div>
            <input
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2"
              placeholder="Or custom model name"
              value={customModelName}
              onChange={(e) => {
                setCustomModelName(e.target.value);
                if (e.target.value) setModelId('');
              }}
              disabled={!canEdit}
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-600">IMEI</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                value={imei}
                onChange={(e) => setImei(e.target.value.replace(/\D/g, '').slice(0, 15))}
                disabled={!canEdit}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-600">Serial</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                disabled={!canEdit}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-600">Color</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                disabled={!canEdit}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-600">Condition</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                value={conditionAtIntake ?? 'good'}
                onChange={(e) => setConditionAtIntake(e.target.value)}
                disabled={!canEdit}
              >
                {CONDITIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Passcode type</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2"
                value={passcodeType ?? 'unknown'}
                onChange={(e) => {
                  setPasscodeType(e.target.value);
                  setPasscode('');
                }}
                disabled={!canEdit}
              >
                {PASSCODE_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
            {passcodeTypeNeedsValue(passcodeType) ? (
              <label className="block text-sm">
                <span className="font-medium text-slate-700">Passcode</span>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-base"
                  type="text"
                  inputMode={passcodeType === 'pin' || passcodeType === 'pattern' ? 'numeric' : 'text'}
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  disabled={!canEdit}
                  placeholder={
                    passcodeType === 'pattern'
                      ? 'Pattern (e.g. 1-2-5-8)'
                      : passcodeType === 'pin'
                        ? 'Device PIN'
                        : 'Device password'
                  }
                />
                <p className="mt-1 text-xs text-slate-500">
                  Enter the code to store on this ticket. Leave blank to keep the current code on
                  file.
                </p>
              </label>
            ) : (
              <p className="text-xs text-slate-500">
                Choose PIN, password, pattern, or fingerprint to enter a code for this device.
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canEdit || busy}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => void handleSubmit()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export function DevicePriceChangeDialog({
  open,
  lines,
  onKeep,
  onUpdate,
  onCancel,
}: {
  open: boolean;
  lines: DeviceEditPricePrompt['repairs'];
  onKeep: () => void;
  onUpdate: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-bold text-slate-950">Repair prices may change</h3>
        <p className="mt-2 text-sm text-slate-600">
          Changing the device updates catalog pricing for attached repairs. Do not change quoted
          prices without choosing explicitly.
        </p>
        <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto text-sm">
          {lines.map((l) => (
            <li key={l.repairId} className="rounded-lg border border-slate-200 px-3 py-2">
              Changing model changes <span className="font-semibold">{l.serviceName}</span> from{' '}
              {formatCurrency(l.previousPrice)} to {formatCurrency(l.newPrice)}.
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold"
            onClick={onKeep}
          >
            Keep current
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white"
            onClick={onUpdate}
          >
            Update price
          </button>
        </div>
      </div>
    </div>
  );
}
